import { useContext, useEffect, useRef, useState } from "react";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import "../../../styles/dashboard/transaction/transactionIndex.css";
import {
  RazorBadgeTwo,
  RazorDateTime,
  RazorInputField,
  RazorTable,
  RazorTableRow,
  RazorTextTrim,
  RazorTwinValue,
} from "../../../components/razor-reuse";
import FilterReusableComp from "../../../components/FilterReusableComp";
import PaginationFragment from "../../../components/fragment/pagination/PaginationFragment";
import NoContentPage from "../../../layout/dashboard/no-content/NoContentPage";
import { useDispatch, useSelector } from "react-redux";
import RootState, {
  PlaidTransactionItem,
  PlaidTransactionQuery,
} from "../../../redux/type";
import { getPlaidTransaction } from "../../../redux/transaction";
import DateRangeComponent from "../../../components/fragment/date-range/DateRangeComponent";
import { useNavigate } from "react-router-dom";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { reactSelectStyleTable } from "../../../utils/Helper";
import ActionContext from "../../../context/ActionContext";
import PlaidTransactionModal from "./PlaidTransactionModal";
import {
  CREDIT_STATUS_OPTIONS,
  CURRENCY_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  formatLabel,
  formatMoney,
  formatPaymentStatus,
  getBankPaymentBadge,
  getCustomerName,
  getOverallStatusBadge,
  getWalletCreditBadge,
} from "./plaidTransactionHelpers";

const PLAID_PERMISSION = "view_partner_transactions";
// The endpoint rejects per_page above 100, but the shared per-page picker
// goes up to 1000, so larger choices are capped.
const MAX_PER_PAGE = 100;
const DEFAULT_PER_PAGE = 15;
const SKELETON_ROWS = 5;

type Option = { label: string; value: string };

interface PlaidFilters {
  search: string;
  status: Option | null;
  creditStatus: Option | null;
  currency: Option | null;
  dateRange: string;
}

const EMPTY_FILTERS: PlaidFilters = {
  search: "",
  status: null,
  creditStatus: null,
  currency: null,
  dateRange: "",
};

const headerList = [
  "Customer",
  "Reference",
  "Amount",
  "Bank Payment",
  "Wallet Credit",
  "Status",
  "Date",
  "Action",
];

const PlaidTransaction = () => {
  const actionCtx = useContext(ActionContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);
  const { loading_plaid, plaid_transaction, plaid_failed } = useSelector(
    (state: RootState) => state.transaction,
  );

  const canView = !!user?.permissions?.includes(PLAID_PERMISSION);

  const [filters, setFilters] = useState<PlaidFilters>(EMPTY_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  // The shared search box debounces with a callback from an older render, so
  // read the latest filters from a ref rather than the closure.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [activeRow, setActiveRow] = useState<PlaidTransactionItem | null>(
    null,
  );

  const perPage = Math.min(
    Number(actionCtx?.perPage?.value) || DEFAULT_PER_PAGE,
    MAX_PER_PAGE,
  );

  // useEffect to check if user has permission to view plaid transactions
  useEffect(() => {
    if (!canView && user?.permissions?.length > 0) {
      navigate(ALL_ROUTES?.DASHBOARD_OVERVIEW);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.permissions]);

  // Every filter change goes back to page 1. Both updates are batched into
  // one render, so only one request is sent. Re-picking the value that's
  // already applied (e.g. "All Status" twice) changes nothing.
  const updateFilters = (patch: Partial<PlaidFilters>) => {
    const current = filtersRef.current;
    const changed = (Object.keys(patch) as (keyof PlaidFilters)[]).some(
      (key) => {
        const next = patch[key];
        const prev = current[key];
        const nextValue = typeof next === "object" ? next?.value : next;
        const prevValue = typeof prev === "object" ? prev?.value : prev;
        return (nextValue || "") !== (prevValue || "");
      },
    );
    if (!changed) return;
    setFilters((prev) => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };

  // Typing only spaces doesn't change the trimmed search, so no refetch.
  const handleSearch = (value: string) => {
    updateFilters({ search: (value || "").trim() });
  };

  // "All ..." options carry an empty value — store them as no filter.
  const toFilterOption = (option: any): Option | null =>
    option?.value ? option : null;

  // A per-page change also returns to page 1. If we're not on page 1 yet,
  // skip this fetch: resetting the page re-runs the effect with page 1.
  const lastPerPage = useRef(perPage);
  useEffect(() => {
    if (!canView) return;

    if (lastPerPage.current !== perPage) {
      lastPerPage.current = perPage;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    const query: PlaidTransactionQuery = {
      page: currentPage,
      per_page: perPage,
      search: filters.search,
      status: filters.status?.value,
      credit_status: filters.creditStatus?.value,
      currency: filters.currency?.value,
      "filter[date]": filters.dateRange,
    };
    dispatch(getPlaidTransaction(query) as any);
  }, [canView, currentPage, perPage, filters, reloadKey, dispatch]);

  const rows: PlaidTransactionItem[] = plaid_transaction?.data || [];
  const meta = plaid_transaction?.meta;

  // If the current page no longer exists (e.g. records were removed while
  // paging), jump back to the last page that does.
  useEffect(() => {
    if (
      !loading_plaid &&
      meta &&
      rows.length < 1 &&
      meta.last_page >= 1 &&
      currentPage > meta.last_page
    ) {
      setCurrentPage(meta.last_page);
    }
  }, [loading_plaid, meta, rows.length, currentPage]);

  const hasActiveFilters =
    !!filters.search ||
    !!filters.status ||
    !!filters.creditStatus ||
    !!filters.currency ||
    !!filters.dateRange;

  const showEmptyState = !loading_plaid && !plaid_failed && rows.length < 1;
  // Only replace the table with an error when there's nothing to show; if a
  // later request fails, the last loaded rows stay (and a toast is shown).
  const showErrorState = !loading_plaid && plaid_failed && rows.length < 1;

  return (
    <>
      <DashboardLayoutMain
        loading={loading_plaid && !plaid_transaction}
        title="Plaid Transactions"
      >
        <div className="transaction-index-wrap-box-smartsend">
          <FilterReusableComp
            defaultSelectOne="Payment Status"
            selectOneList={PAYMENT_STATUS_OPTIONS}
            selectOneValue={filters.status}
            onSelectOne={(param) =>
              updateFilters({ status: toFilterOption(param) })
            }
            defaultSelectTwo="Credit Status"
            selectTwoList={CREDIT_STATUS_OPTIONS}
            selectTwoValue={filters.creditStatus}
            onSelectTwo={(param) =>
              updateFilters({ creditStatus: toFilterOption(param) })
            }
            hideExport
            searchPlaceholder="Search by reference, Plaid ID or customer"
            onSearch={handleSearch}
            moreComponents={
              <>
                <RazorInputField
                  color="black-light"
                  type="select"
                  className="select-input"
                  selectStyles={reactSelectStyleTable}
                  selectOption={CURRENCY_OPTIONS}
                  value={
                    filters.currency ||
                    CURRENCY_OPTIONS[CURRENCY_OPTIONS.length - 1]
                  }
                  onChange={(e: any) =>
                    updateFilters({ currency: toFilterOption(e) })
                  }
                  placeholder="Currency"
                />
                <DateRangeComponent
                  onSet={(param) => updateFilters({ dateRange: param || "" })}
                  value={filters.dateRange}
                  onCancel={() => updateFilters({ dateRange: "" })}
                />
              </>
            }
          />
          {/* filter section end */}
          {/* table start */}
          <div className="table-section-box">
            {showErrorState ? (
              <div className="flex flex-col items-center">
                <NoContentPage
                  title="Couldn't load Plaid transactions"
                  text="Something went wrong while fetching transactions"
                />
                <button
                  type="button"
                  onClick={() => setReloadKey((key) => key + 1)}
                  className="mt-[12px] rounded-[8px] border border-[#734792] px-[16px] py-[8px] text-[13px] font-medium text-[#734792]"
                >
                  Retry
                </button>
              </div>
            ) : showEmptyState ? (
              <NoContentPage
                title={
                  hasActiveFilters
                    ? "No matching Plaid transactions"
                    : "No Plaid transactions yet"
                }
                text={
                  hasActiveFilters
                    ? "Try changing or clearing your search and filters"
                    : "Bank top-ups made through Plaid will appear here"
                }
              />
            ) : (
              <RazorTable headerList={headerList}>
                {rows.length < 1 && loading_plaid
                  ? Array.from({ length: SKELETON_ROWS }).map((_, idx) => (
                      <RazorTableRow key={`skeleton-${idx}`} loading />
                    ))
                  : rows.map((chi, idx) => (
                      <RazorTableRow
                        key={chi?.id || idx}
                        loading={loading_plaid}
                        onRowClick={() => setActiveRow(chi)}
                        one={
                          <RazorTwinValue
                            one={RazorTextTrim(getCustomerName(chi), 30)}
                            two={RazorTextTrim(chi?.customer?.email || "", 30)}
                          />
                        }
                        two={RazorTextTrim(chi?.reference || "---", 22)}
                        three={
                          <RazorTwinValue
                            one={formatMoney(
                              chi?.bank_payment?.amount,
                              chi?.bank_payment?.currency ||
                                chi?.wallet_currency,
                            )}
                            two={
                              chi?.bank_payment?.currency ||
                              chi?.wallet_currency ||
                              ""
                            }
                          />
                        }
                        four={
                          chi?.bank_payment?.status ? (
                            <RazorBadgeTwo
                              className={getBankPaymentBadge(
                                chi.bank_payment.status,
                              )}
                              text={formatPaymentStatus(
                                chi.bank_payment.status,
                              )}
                            />
                          ) : (
                            "---"
                          )
                        }
                        five={
                          chi?.wallet_credit?.status ? (
                            <RazorBadgeTwo
                              className={getWalletCreditBadge(
                                chi.wallet_credit.status,
                              )}
                              text={formatLabel(chi.wallet_credit.status)}
                            />
                          ) : (
                            "---"
                          )
                        }
                        six={
                          chi?.status ? (
                            <RazorBadgeTwo
                              className={getOverallStatusBadge(chi.status)}
                              text={formatLabel(chi.status)}
                            />
                          ) : (
                            "---"
                          )
                        }
                        seven={
                          chi?.created_at ? (
                            <RazorDateTime date={chi.created_at} />
                          ) : (
                            "---"
                          )
                        }
                        eight={
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveRow(chi);
                            }}
                            className="text-[13px] font-medium text-[#734792] underline whitespace-nowrap"
                          >
                            View details
                          </button>
                        }
                      />
                    ))}
              </RazorTable>
            )}
          </div>
          {/* table end */}
          {/* pagination start */}
          <PaginationFragment
            currentPage={meta?.current_page || currentPage}
            perPage={meta?.per_page || perPage}
            totalPage={meta?.total || 0}
            onPageChange={(num) => setCurrentPage(num)}
            hidePagination={loading_plaid || rows.length < 1}
            showPerPage
          />
          {/* pagination end */}
        </div>
      </DashboardLayoutMain>

      <PlaidTransactionModal
        visible={!!activeRow}
        details={activeRow}
        onClose={() => setActiveRow(null)}
      />
    </>
  );
};

export default PlaidTransaction;
